import { cn } from "@faire/prototype-playground/lib/utils";
import { PlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import React, { useEffect, useRef, useState } from "react";

import { useAutoplayInView } from "../../hooks/useAutoplayInView";
import { useHlsVideoSource } from "../../hooks/useHlsVideoSource";
import { useImageGalleryIndex } from "../../hooks/useImageGalleryIndex";
import { useImageSlideshow } from "../../hooks/useImageSlideshow";
import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

import { HoverGalleryControls } from "./HoverGalleryControls";
import { ProductTileSaveIcon } from "./ProductTileSaveIcon";
import {
  SlideshowVideo,
  type SlideshowAsset,
  type SlideshowPlaybackIndicator,
  VideoPlaybackBar,
} from "./SlideshowVideo";

export function HeartIcon({
  filled,
  hovered = false,
}: {
  filled?: boolean;
  /** Hovering the heart button darkens the (unfilled) grey fill a touch, mirroring production. */
  hovered?: boolean;
}) {
  return <ProductTileSaveIcon filled={filled} hovered={hovered} />;
}

/** Bare play triangle sitting directly on the image. Same translucent fill as the save icon, no outline; a matching stroke with round joins slightly rounds the corners. */
const PlayTriangleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g opacity={0.5}>
      <path
        d="M5.5 3L21 12L5.5 21Z"
        fill="#333333"
        stroke="#333333"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

/** Play triangle for the white circle button, sized and weighted like `FindSimilarIcon` so the circular controls read as one set. */
const PlayTriangleIconDark = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M5.25 3L12.25 8L5.25 13Z"
      fill="#333333"
      stroke="#333333"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function ShippingPerkIcon() {
  return (
    <svg
      className="h-3 w-3 flex-shrink-0"
      viewBox="0 0 16 16"
      fill="#36676a"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h5A1.5 1.5 0 0 1 10 3.5V5h1.379a1.5 1.5 0 0 1 1.06.44l1.622 1.62A1.5 1.5 0 0 1 14.5 8.12v2.13a1.25 1.25 0 0 1-1.25 1.25h-.19a2 2 0 0 1-3.62 0H7.56a2 2 0 0 1-3.62 0H3.25A1.25 1.25 0 0 1 2 10.25V3.5ZM5.5 11.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm5.5.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0ZM11 6.5V8h2.12L11.5 6.5H11Z" />
    </svg>
  );
}

export function StarIcon() {
  return (
    <svg
      width="10"
      height="9"
      viewBox="0 0 10 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.99445 0.218149L6.12516 2.51029C6.15336 2.56719 6.19492 2.61641 6.2463 2.65373C6.29768 2.69105 6.35734 2.71536 6.42016 2.72458L8.95016 3.09315C9.02227 3.10403 9.0899 3.13483 9.14543 3.18209C9.20096 3.22935 9.24219 3.29118 9.26447 3.36061C9.28675 3.43004 9.28919 3.50431 9.27152 3.57506C9.25384 3.6458 9.21676 3.71021 9.16445 3.76101L7.33302 5.54672C7.28756 5.59098 7.25358 5.64567 7.23404 5.70604C7.2145 5.76641 7.20998 5.83064 7.22088 5.89315L7.65302 8.41601C7.66573 8.48843 7.65779 8.56296 7.6301 8.63108C7.60241 8.6992 7.5561 8.75813 7.49646 8.80115C7.43683 8.84416 7.36628 8.8695 7.29291 8.87428C7.21954 8.87906 7.1463 8.86307 7.08159 8.82815L4.82516 7.64172C4.76899 7.61219 4.70648 7.59676 4.64302 7.59676C4.57956 7.59676 4.51705 7.61219 4.46088 7.64172L2.19874 8.83172C2.13403 8.86664 2.06079 8.88263 1.98742 8.87785C1.91405 8.87308 1.8435 8.84773 1.78387 8.80472C1.72423 8.76171 1.67792 8.70277 1.65023 8.63465C1.62254 8.56654 1.61459 8.492 1.62731 8.41958L2.05945 5.89672C2.07035 5.83421 2.06583 5.76998 2.04629 5.70961C2.02675 5.64925 1.99277 5.59456 1.94731 5.55029L0.118735 3.76315C0.0664235 3.71235 0.0293415 3.64795 0.0116694 3.5772C-0.00600279 3.50646 -0.00356244 3.43218 0.0187153 3.36275C0.040993 3.29332 0.0822223 3.23149 0.137756 3.18423C0.193289 3.13698 0.260919 3.10617 0.333021 3.09529L2.86588 2.72458C2.92871 2.71536 2.98837 2.69105 3.03974 2.65373C3.09112 2.61641 3.13269 2.56719 3.16088 2.51029L4.29159 0.218149C4.32402 0.15265 4.37411 0.0975173 4.43621 0.0589713C4.4983 0.0204253 4.56993 0 4.64302 0C4.71611 0 4.78774 0.0204253 4.84984 0.0589713C4.91193 0.0975173 4.96202 0.15265 4.99445 0.218149V0.218149Z"
        fill="#333333"
      />
    </svg>
  );
}

export function PlusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.00009 1V11M1 5.96923H11"
        stroke="#FFFFFF"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SpinnerIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="animate-spin"
    >
      <path
        d="M6 1.5A4.5 4.5 0 1 1 1.5 6"
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ChevronDownIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="#333333"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 6L5 8L9 4"
        stroke="#333333"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FindSimilarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M9.89584 11.5081C8.87995 12.524 7.50212 13.0947 6.06544 13.0947C4.62876 13.0947 3.25092 12.524 2.23504 11.5081C1.21916 10.4923 0.648438 9.11442 0.648438 7.67774C0.648437 6.24106 1.21916 4.86323 2.23504 3.84734C3.25092 2.83146 4.62876 2.26074 6.06544 2.26074C7.50212 2.26074 8.87995 2.83146 9.89584 3.84734C10.9117 4.86323 11.4824 6.24106 11.4824 7.67774C11.4824 9.11442 10.9117 10.4923 9.89584 11.5081ZM9.89584 11.5081L13.6485 15.2608"
        stroke="#333333"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.1309 -0.524414C11.4483 -0.524412 11.846 -0.345162 11.9854 0.0771484L12.0098 0.164062L12.1299 0.65918C12.4134 1.74738 12.7248 2.38784 13.1689 2.83203C13.6765 3.33959 14.4403 3.67373 15.8359 3.99121C16.321 4.10154 16.5254 4.53159 16.5254 4.87012C16.5254 5.20854 16.3216 5.63853 15.8369 5.74902C14.4411 6.06651 13.6766 6.40058 13.1689 6.9082C12.6614 7.41577 12.3272 8.17959 12.0098 9.5752C11.8994 10.0602 11.4694 10.2646 11.1309 10.2646C10.7923 10.2646 10.3623 10.0602 10.252 9.5752C9.93448 8.17959 9.60034 7.41577 9.09277 6.9082C8.58511 6.40058 7.82065 6.06651 6.4248 5.74902C5.94006 5.63856 5.73633 5.20855 5.73633 4.87012C5.73633 4.53168 5.94005 4.10168 6.4248 3.99121C7.82063 3.67369 8.5851 3.33971 9.09277 2.83203C9.60045 2.32435 9.93443 1.55989 10.252 0.164062C10.3624 -0.320691 10.7924 -0.524416 11.1309 -0.524414Z"
        fill="#333333"
        stroke="white"
        strokeWidth="1.25"
      />
    </svg>
  );
}

export function InsiderKeyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="6" cy="6" r="3" stroke="#36676a" strokeWidth="1.2" />
      <circle cx="6" cy="6" r="1.2" fill="#36676a" />
      <path
        d="M8.5 8.5L13 13"
        stroke="#36676a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M10.5 11L11.5 12"
        stroke="#36676a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Free-shipping perk as a tag, matching the shipping/Insider program-tag chip
 * (`PROGRAM_TAG_CHIP[1]`) so every shipping affordance on a tile reads as the
 * same kind of badge — both the unconditional "Free shipping" and the
 * threshold "Free shipping over $X" variants.
 */
export function FreeShippingBadge({
  label,
}: {
  label: string;
}): React.ReactElement {
  return (
    <div className="box-border flex h-[20px] w-fit flex-shrink-0 items-center self-start rounded-[4px] bg-[#F2F5F5] px-[4px] py-[2px]">
      <span
        className="text-[11px] leading-[16px] font-normal tracking-[0.15px] whitespace-nowrap text-[#154548]"
        style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
      >
        {label}
      </span>
    </div>
  );
}

export const SWATCH_SETS = [
  ["#b0b8c1", "#c0392b", "#e8856a", "#5a7a4a", "#7a9ab5", "#d4c5a9"],
  ["#f5e6d3", "#8b4513", "#2c3e50", "#27ae60", "#e74c3c", "#9b59b6"],
  ["#ecf0f1", "#e67e22", "#1abc9c", "#34495e", "#f39c12", "#2980b9"],
  ["#fadbd8", "#a9cce3", "#a9dfbf", "#fdebd0", "#d2b4de", "#d5d8dc"],
];

export const ATTRIBUTE_SETS = [
  ["100% wool", "Made in USA"],
  ["100% wool"],
  ["Made in USA"],
  ["100% wool", "Made in USA"],
  ["Made in USA"],
];

/** 1 = shipping / Insider, 2 = sale (Slate border + sale text), 3 = Faire Source. */
export type ProgramTagChipKind = 1 | 2 | 3;

export type ProgramTagChip = {
  label: string;
  chipKind: ProgramTagChipKind;
};

export type TileProduct = {
  id: string;
  imageUrl: string;
  brandName: string;
  brandAvatarUrl?: string;
  productName: string;
  price: number;
  msrp: number;
  minOrder: string;
  freeShipping: boolean;
  freeShippingLabel?: string;
  isBestseller: boolean;
  rating?: number;
  reviewCount?: number;
  swatchColors: string[];
  extraSwatches: number;
  attributes: string[];
  programTagChips: ProgramTagChip[];
  searchMatchTagLabels: string[];
  discount?: string;
  minQty: number;
  productMetadata?: string;
};

export type AtcButtonColor =
  | "dark"
  | "light"
  | "neutral"
  | "neutral2"
  | "custom";

export type PlayIconStyle = "standalone" | "circle-button";

export interface ProductTileProps {
  product: TileProduct;
  justified?: boolean;
  showProgramTags?: boolean;
  showSearchMatchTags?: boolean;
  showBrandAvatar?: boolean;
  showFindSimilarOnImage?: boolean;
  /** Invoked when the tile's "Find similar" control is activated. Opt-in: when omitted the control stays inert, matching its long-standing decorative behavior. */
  onFindSimilarClick?: () => void;
  /** When true (ProductTileATC + `showFindSimilarOnImage` only, default), the "Find similar" icon button grows into a pill revealing its label on hover. Set to false for touch/mobile tiles, which have no meaningful hover-to-expand gesture — it stays a fixed-size icon button with just a hover outline. */
  findSimilarExpandsOnHover?: boolean;
  /** When true (ProductTileATC only), moves the Add to cart control from the metadata row onto the image as a circular icon button (bottom-right, mirroring `showFindSimilarOnImage`) instead of the full text button/quantity picker. */
  showAddToCartOnImage?: boolean;
  showAddToCartPicker?: boolean;
  atcButtonColor?: AtcButtonColor;
  atcButtonCustomColor?: string;
  fillButtonWidth?: boolean;
  /** When provided (ProductTileATC only), plays a looping video in place of the static image, with the image as poster/fallback. Mutually exclusive with `slideshowImageUrls` / `galleryImageUrls`. */
  videoUrl?: string;
  /** When true (with `videoUrl`), the video only shows/plays while the tile is hovered; otherwise the static product image shows. */
  playVideoOnHoverOnly?: boolean;
  /** Controls the resting play affordance on hover-to-play video tiles. */
  playIconStyle?: PlayIconStyle;
  /** Touch/mobile substitute for `playVideoOnHoverOnly`: plays the video once the tile scrolls into view instead of waiting for a pointer hover. */
  autoplayOnView?: boolean;
  /** When true (with `videoUrl`, ProductTileATC only), replaces hover/autoplay entirely with a 2-slide swipeable gallery: the static product image, then the video — swiping (or dragging) to the second slide starts playback. A 2-slide indicator at the bottom signals it's swipable, using a play triangle for the video slide. Intended for touch/mobile tiles where there's no hover state; takes priority over `playVideoOnHoverOnly` if both are set. */
  swipeToVideoOnTouch?: boolean;
  /** When provided (ProductTileATC only, and no `videoUrl`), plays a generated crossfade slideshow of the product's images in the video's place, following the same play/hover rules as `videoUrl`. */
  slideshowAsset?: SlideshowAsset;
  /** Playback affordance along the bottom of a `slideshowAsset` (dots or segmented progress bars). */
  slideshowPlaybackIndicator?: SlideshowPlaybackIndicator;
  /** When provided (ProductTileATC only, and no `videoUrl`), cycles through these images (3-5 recommended) every 2s instead of showing a single static image. */
  slideshowImageUrls?: string[];
  /** Phase-shifts this tile's slideshow changes relative to other tiles' (see `useImageSlideshow`), so a grid of slideshow tiles doesn't flip in lockstep. */
  slideshowOffsetMs?: number;
  /** When provided (ProductTileATC only, and no `videoUrl`/`slideshowImageUrls`), shows a click-driven gallery (hover arrows + dot indicator) through these images instead of auto-cycling. */
  galleryImageUrls?: string[];
  /** When true (ProductTileATC only), shows a "Promoted" label at the bottom of the tile metadata, below any program tag badges. */
  showPromotedLabel?: boolean;
  /** When true with `showPromotedLabel`, renders Promoted under the Add to cart button instead of above it. */
  showPromotedBelowAtc?: boolean;
  /** Product name clamp. Defaults to 2 lines. */
  productNameLineClamp?: 1 | 2;
  /** Swatch chip diameter in pixels. Defaults to 16. */
  swatchSizePx?: number;
  /** Pull the Add to cart button closer to the brand-info block (4px instead of 8px). */
  compactAtcSpacing?: boolean;
  /** Render free-shipping copy as an icon + text line instead of a chip badge. */
  shippingAsIconLine?: boolean;
  /** When true, removes the image's rounded corners (e.g. for full-bleed native-app mobile grids, which use square tiles). Defaults to the standard rounded corners. */
  squareCorners?: boolean;
  /** When provided (ProductTileATC only), 1-2 personalized "why this tile" bullets rendered below the Add to cart button, with the periwinkle rule used for assistant-generated copy. */
  rationale?: string[];
  /** When true, show the wholesale price and a one-line product name together, tighten brand spacing, and hide MSRP, minimum, and rating. */
  condensedMetadata?: boolean;
  /** When true, drops every pointer-hover affordance (find similar expansion, image-control hover outlines). Set on touch/mobile previews, where a hover state can never be reached on the real device. */
  disableHoverAffordances?: boolean;
  /** When true (ProductTileATCCompact only), sizes the min-order/rating line to match ProductTileATC's type-fs-paragraph instead of the smaller compact default. */
  useDefaultMetadataSize?: boolean;
}

const GRAPHIK = "Graphik, sans-serif" as const;

const ATC_QTY_OPTIONS = [4, 8, 12, 16, 20];
const ATC_QTY_STEP = 4;

type CartState = "empty" | "loading" | "in-cart";

const PROGRAM_TAG_CHIP: Record<
  ProgramTagChipKind,
  {
    containerClass: string;
    labelClass: string;
    labelStyle: React.CSSProperties;
  }
> = {
  1: {
    containerClass:
      "rounded-[4px] box-border h-[20px] flex items-center px-[4px] py-[2px] flex-shrink-0 bg-[#F2F5F5]",
    labelClass:
      "font-normal text-[11px] leading-[16px] tracking-[0.15px] text-[#154548] whitespace-nowrap",
    labelStyle: { fontFamily: GRAPHIK, fontWeight: 400 },
  },
  2: {
    containerClass:
      "rounded-[4px] box-border h-[20px] flex items-center p-[2px] flex-shrink-0 border border-[var(--slate-color-border-muted)] bg-fs-surface-primary",
    labelClass:
      "font-normal text-[11px] leading-[16px] tracking-[0.15px] text-[var(--slate-color-message-text-sale)] whitespace-nowrap",
    labelStyle: { fontFamily: GRAPHIK, fontWeight: 400 },
  },
  3: {
    containerClass:
      "rounded-[4px] box-border h-[20px] flex items-center px-[4px] py-[2px] flex-shrink-0 bg-[#F6F2E8]",
    labelClass:
      "font-normal text-[11px] leading-[16px] tracking-[0.15px] text-[#735B21] whitespace-nowrap",
    labelStyle: { fontFamily: GRAPHIK, fontWeight: 400 },
  },
};

const SEARCH_MATCH_TAG_CHIP = {
  containerClass:
    "rounded-[4px] box-border h-[20px] flex items-center p-[2px] flex-shrink-0 border border-[var(--slate-color-border-muted)] bg-fs-surface-primary",
  labelClass:
    "font-normal text-[11px] leading-[16px] tracking-[0.15px] text-[#333] whitespace-nowrap",
  labelStyle: { fontFamily: GRAPHIK, fontWeight: 400 },
};

function ProgramAndSearchTagsRow({
  programChips,
  searchMatchLabels,
  showSearchMatchTags,
}: {
  programChips: ProgramTagChip[];
  searchMatchLabels: string[];
  showSearchMatchTags: boolean;
}): React.ReactElement | null {
  const visibleSearch = showSearchMatchTags ? searchMatchLabels : [];
  if (programChips.length === 0 && visibleSearch.length === 0) {
    return null;
  }
  const ariaLabel =
    showSearchMatchTags && visibleSearch.length > 0
      ? "Program and search tags"
      : "Program tags";
  return (
    <div
      className="mt-[2px] flex w-full flex-wrap items-start gap-[4px]"
      aria-label={ariaLabel}
    >
      {programChips.map((chip, idx) => {
        const styles = PROGRAM_TAG_CHIP[chip.chipKind];
        return (
          <div key={`p-${chip.label}-${idx}`} className={styles.containerClass}>
            <span className={styles.labelClass} style={styles.labelStyle}>
              {chip.label}
            </span>
          </div>
        );
      })}
      {visibleSearch.map((label, idx) => (
        <div
          key={`s-${label}-${idx}`}
          className={SEARCH_MATCH_TAG_CHIP.containerClass}
        >
          <span
            className={SEARCH_MATCH_TAG_CHIP.labelClass}
            style={SEARCH_MATCH_TAG_CHIP.labelStyle}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ProductTile({
  product,
  showProgramTags = false,
  showSearchMatchTags = false,
  showBrandAvatar = false,
  showFindSimilarOnImage = false,
  onFindSimilarClick,
}: ProductTileProps): React.ReactElement {
  const [favorited, setFavorited] = useState(false);
  const [heartHovered, setHeartHovered] = useState(false);
  const { src: imageSrc, onError: onImageError } =
    usePlaygroundImageWithFallback(product.imageUrl);
  const hasSale = product.discount != null;
  const hasDiscount = product.msrp > product.price;

  return (
    <div className="flex w-full min-w-0 cursor-pointer flex-col items-start gap-[8px] [&_button]:cursor-pointer [&_button:disabled]:cursor-default">
      <div
        className="relative w-full overflow-hidden rounded-[4px]"
        style={{ aspectRatio: "1 / 1" }}
      >
        <img
          src={imageSrc}
          alt={product.productName}
          className="absolute inset-0 h-full w-full rounded-[4px] object-cover"
          referrerPolicy="no-referrer"
          onError={onImageError}
        />
        <div
          className="absolute inset-0 rounded-[4px]"
          style={{ background: "rgba(51,51,51,0.03)" }}
        />

        {product.isBestseller ? (
          <div className="bg-fs-surface-primary absolute top-[8px] left-[8px] rounded-[2px] px-[8px] py-0">
            <span
              className="text-[12px] leading-[16px] tracking-[0.15px] whitespace-nowrap text-[#333]"
              style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
            >
              Bestseller
            </span>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => setFavorited((f) => !f)}
          onMouseEnter={() => setHeartHovered(true)}
          onMouseLeave={() => setHeartHovered(false)}
          className="absolute top-2 right-2 flex size-6 items-center justify-center"
          aria-label="Favorite"
        >
          <HeartIcon filled={favorited} hovered={heartHovered} />
        </button>

        {showFindSimilarOnImage ? (
          <button
            type="button"
            onClick={onFindSimilarClick}
            className="absolute bottom-[8px] left-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full"
            style={{
              background: "#FFFFFF",
              boxShadow: "0px 0px 1px 0px rgba(0,0,0,0.15)",
            }}
            aria-label="Find similar"
          >
            <FindSimilarIcon />
          </button>
        ) : null}
      </div>

      <div className="flex w-full flex-col items-start gap-[4px]">
        <div
          className={cn(
            "flex w-full flex-col items-start gap-0",
            showBrandAvatar ? "min-h-[86px]" : ""
          )}
        >
          {product.swatchColors.length > 0 ? (
            <div className="mt-[2px] mb-[5px] flex items-center gap-[4px]">
              {product.swatchColors.map((color, i) => (
                <div
                  key={i}
                  className="h-[16px] w-[16px] flex-shrink-0 rounded-full border border-(--slate-color-grey-1000)/10"
                  style={{ backgroundColor: color }}
                />
              ))}
              {product.extraSwatches > 0 ? (
                <span
                  className="w-[19px] text-center text-[12px] leading-[16px] tracking-[0.15px] text-[#757575]"
                  style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
                >
                  +{product.extraSwatches}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="flex w-full items-baseline gap-[8px]">
            <span
              className={cn(
                "flex-shrink-0 text-[18px] leading-[26px] tracking-[0.15px]",
                hasSale ? "text-[#7d3e1e]" : "text-[#333]"
              )}
              style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
            >
              $
              {product.price % 1 === 0
                ? product.price
                : product.price.toFixed(2)}
            </span>
            {hasSale && hasDiscount ? (
              <span
                className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#757575] line-through"
                style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
              >
                $
                {product.msrp % 1 === 0
                  ? product.msrp
                  : product.msrp.toFixed(2)}
              </span>
            ) : null}
            {hasDiscount ? (
              <span
                className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
                style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
              >
                MSRP ${Math.round(product.msrp)}
              </span>
            ) : null}
          </div>

          <p
            className="w-full truncate text-[14px] leading-[20px] tracking-[0.15px] text-[#333]"
            style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
          >
            {product.productName}
          </p>

          {product.productMetadata ? (
            <p
              className="mt-[1px] text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
              style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
            >
              {product.productMetadata}
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            "flex w-full items-start",
            showBrandAvatar ? "gap-[8px]" : ""
          )}
        >
          {showBrandAvatar ? (
            <BrandAvatar src={product.brandAvatarUrl} />
          ) : null}
          <div
            className={cn(
              "flex flex-col items-start gap-[1px]",
              showBrandAvatar ? "min-w-0 flex-1" : "w-full"
            )}
          >
            <div className="w-full overflow-hidden pb-[2px]">
              <span className="type-fs-paragraph block overflow-hidden text-ellipsis whitespace-nowrap text-[#333] underline underline-offset-[2px]">
                {product.brandName}
              </span>
            </div>

            <div className="flex w-full items-center gap-[4px]">
              <span className="type-fs-paragraph flex-shrink-0 whitespace-nowrap text-[#333]">
                {product.minOrder}
              </span>
              {product.rating != null ? (
                <>
                  <span
                    aria-hidden
                    className="type-fs-paragraph flex-shrink-0 text-[#333]"
                  >
                    ·
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-[4px]">
                    <StarIcon />
                    <span className="type-fs-paragraph whitespace-nowrap text-[#333]">
                      {product.rating.toFixed(1)}
                    </span>
                  </div>
                </>
              ) : null}
            </div>

            {showProgramTags &&
            (product.programTagChips.length > 0 ||
              (showSearchMatchTags &&
                product.searchMatchTagLabels.length > 0)) ? (
              <ProgramAndSearchTagsRow
                programChips={product.programTagChips}
                searchMatchLabels={product.searchMatchTagLabels}
                showSearchMatchTags={showSearchMatchTags}
              />
            ) : null}

            {product.freeShipping && product.freeShippingLabel !== undefined ? (
              <FreeShippingBadge label={product.freeShippingLabel} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandAvatar({ src }: { src?: string }): React.ReactElement {
  return (
    <div className="relative h-[24px] w-[24px] flex-shrink-0 overflow-hidden rounded-full bg-[#e5e5e5]">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : null}
      <div className="pointer-events-none absolute inset-0 bg-(--slate-color-grey-1000)/[0.02]" />
    </div>
  );
}

/**
 * The image area for `swipeToVideoOnTouch`: a 2-slide horizontal carousel
 * (static product image, then video) that responds to touch drag (real
 * mobile) and mouse drag (desktop testing/trackpad), snapping to whichever
 * slide is closer once the drag passes a distance threshold. Manipulates
 * the image/video elements' `transform` directly via refs during a drag
 * (rather than React state) so dragging stays smooth at 60fps; only commits
 * the slide change to React state (`onActiveSlideChange`) once the drag
 * ends. A 2-slide indicator at the bottom signals there's a second slide —
 * a dot for the image, a play triangle for the video.
 */
export function SwipeVideoSlides({
  imageSrc,
  imageAlt,
  videoRef,
  videoUrl,
  isHlsVideo,
  activeSlide,
  onActiveSlideChange,
  onImageError,
  onVideoError,
  onVideoLoadedData,
  imageRadiusClass,
}: {
  imageSrc: string;
  imageAlt: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  videoUrl?: string;
  isHlsVideo: boolean;
  activeSlide: 0 | 1;
  onActiveSlideChange: (slide: 0 | 1) => void;
  onImageError: () => void;
  onVideoError: () => void;
  onVideoLoadedData: () => void;
  imageRadiusClass: string;
}): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageSlideRef = useRef<HTMLImageElement>(null);
  const dragStateRef = useRef<{ startX: number; startSlide: 0 | 1 } | null>(
    null
  );

  const setTransition = (value: string) => {
    if (imageSlideRef.current) imageSlideRef.current.style.transition = value;
    if (videoRef.current) videoRef.current.style.transition = value;
  };

  const applyTransform = (slide: 0 | 1, dragPercent = 0) => {
    const base = -(slide * 100) + dragPercent;
    if (imageSlideRef.current) {
      imageSlideRef.current.style.transform = `translateX(${base}%)`;
    }
    if (videoRef.current) {
      videoRef.current.style.transform = `translateX(${base + 100}%)`;
    }
  };

  useEffect(() => {
    setTransition("transform 250ms ease");
    applyTransform(activeSlide);
    // Only the committed slide should animate the snap — a drag in progress
    // manipulates the transform directly and shouldn't be reset mid-gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide]);

  const handleDragMove = (clientX: number): void => {
    const state = dragStateRef.current;
    if (!state || !containerRef.current) return;
    const width = containerRef.current.clientWidth || 1;
    let deltaPercent = ((clientX - state.startX) / width) * 100;
    // Resist dragging past either edge — there are only 2 slides.
    deltaPercent =
      state.startSlide === 0
        ? Math.min(0, Math.max(-100, deltaPercent))
        : Math.max(0, Math.min(100, deltaPercent));
    applyTransform(state.startSlide, deltaPercent);
  };

  const handleDragEnd = (clientX: number): void => {
    const state = dragStateRef.current;
    if (!state || !containerRef.current) return;
    const width = containerRef.current.clientWidth || 1;
    const deltaPercent = ((clientX - state.startX) / width) * 100;
    dragStateRef.current = null;
    const SWIPE_THRESHOLD_PERCENT = 15;
    let nextSlide = state.startSlide;
    if (state.startSlide === 0 && deltaPercent < -SWIPE_THRESHOLD_PERCENT) {
      nextSlide = 1;
    } else if (
      state.startSlide === 1 &&
      deltaPercent > SWIPE_THRESHOLD_PERCENT
    ) {
      nextSlide = 0;
    }
    setTransition("transform 250ms ease");
    applyTransform(nextSlide);
    onActiveSlideChange(nextSlide);
  };

  const handleDragStart = (clientX: number): void => {
    dragStateRef.current = { startX: clientX, startSlide: activeSlide };
    setTransition("none");
  };

  // Mouse drags can leave the tile's bounds mid-gesture, so track them at
  // the window level (touch doesn't need this — touchmove/touchend keep
  // firing on the original target regardless of finger position).
  useEffect(() => {
    const onMove = (e: MouseEvent) => handleDragMove(e.clientX);
    const onUp = (e: MouseEvent) => handleDragEnd(e.clientX);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // Handlers close over `dragStateRef` (stable) rather than reactive
    // state, so they never go stale — safe to register once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 h-full w-full select-none"
      style={{ touchAction: "pan-y" }}
      onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
      onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}
      onMouseDown={(e) => {
        e.preventDefault();
        handleDragStart(e.clientX);
      }}
    >
      <img
        ref={imageSlideRef}
        src={imageSrc}
        alt={imageAlt}
        className={cn(
          "absolute inset-0 h-full w-full object-cover",
          imageRadiusClass
        )}
        style={{ transform: "translateX(0%)" }}
        referrerPolicy="no-referrer"
        onError={onImageError}
        draggable={false}
      />
      <video
        ref={videoRef}
        // HLS playback is driven by `useHlsVideoSource` so the preloaded
        // stream can sit paused on frame 0; only the plain-mp4 path still
        // needs the element to start itself.
        autoPlay={!isHlsVideo}
        loop
        muted
        playsInline
        className={cn(
          "absolute inset-0 h-full w-full cursor-pointer object-cover",
          imageRadiusClass
        )}
        // No poster: the slide is meant to reveal the video's own first
        // frame, and the product image can look nothing like it. The neutral
        // backdrop only shows in the gap before frame 0 decodes.
        style={{ transform: "translateX(100%)", backgroundColor: "#e5e5e5" }}
        onLoadedData={onVideoLoadedData}
        onError={onVideoError}
      >
        {isHlsVideo ? null : <source src={videoUrl} type="video/mp4" />}
      </video>
      <div className="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
        {([0, 1] as const).map((slide) => {
          const color =
            slide === activeSlide ? "#ffffff" : "rgba(255, 255, 255, 0.5)";
          // Slide 1 is always the video, so its indicator is a play triangle
          // rather than a dot — it tells you what swiping over will reveal.
          return slide === 1 ? (
            <svg key={slide} width="6" height="7" viewBox="0 0 14 16">
              <path
                d="M1.5 1.098a1 1 0 0 1 1.517-.858l10.028 6.098a1 1 0 0 1 0 1.71L3.017 14.146A1 1 0 0 1 1.5 13.288V1.098Z"
                className="transition-colors duration-150"
                style={{ fill: color }}
              />
            </svg>
          ) : (
            <span
              key={slide}
              className="block h-1.5 w-1.5 rounded-full transition-colors duration-150"
              style={{ backgroundColor: color }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function ProductTileATC({
  product,
  justified = true,
  showFindSimilarOnImage = false,
  onFindSimilarClick,
  findSimilarExpandsOnHover = true,
  showAddToCartOnImage = false,
  showAddToCartPicker = false,
  atcButtonColor = "dark",
  atcButtonCustomColor,
  fillButtonWidth = false,
  videoUrl,
  playVideoOnHoverOnly = false,
  playIconStyle = "standalone",
  autoplayOnView = false,
  swipeToVideoOnTouch = false,
  slideshowAsset,
  slideshowPlaybackIndicator = "none",
  slideshowImageUrls,
  slideshowOffsetMs = 0,
  galleryImageUrls,
  showPromotedLabel = false,
  showPromotedBelowAtc = false,
  productNameLineClamp = 2,
  swatchSizePx = 16,
  compactAtcSpacing = false,
  shippingAsIconLine = false,
  squareCorners = false,
  rationale,
  condensedMetadata = false,
  disableHoverAffordances = false,
}: ProductTileProps): React.ReactElement {
  const imageRadiusClass = squareCorners ? "" : "rounded-[4px]";
  const imageControlHoverClass = disableHoverAffordances
    ? ""
    : "hover:shadow-[0_0_0_1px_#222]";
  const useDarkIcon = atcButtonColor !== "dark";
  const resolvedCustomColor = atcButtonCustomColor ?? "#F5F2EC";
  const atcButtonBg =
    atcButtonColor === "light"
      ? "#FFFFFF"
      : atcButtonColor === "neutral"
        ? "#F5F2EC"
        : atcButtonColor === "neutral2"
          ? "#E7E2D9"
          : atcButtonColor === "custom"
            ? resolvedCustomColor
            : "#333333";
  const atcButtonBorder =
    atcButtonColor === "neutral"
      ? "1px solid #F5F2EC"
      : atcButtonColor === "neutral2"
        ? "1px solid #E7E2D9"
        : atcButtonColor === "custom"
          ? `1px solid ${resolvedCustomColor}`
          : "1px solid #333333";
  // Figma "Button states": the beige default (#E7E2D9) lightens to #F5F2EC on
  // hover. The other fills have no spec, so they shift by the same direction.
  const atcButtonHoverBg =
    atcButtonColor === "light"
      ? "#F5F2EC"
      : atcButtonColor === "neutral"
        ? "#FAF8F4"
        : atcButtonColor === "neutral2"
          ? "#F5F2EC"
          : atcButtonColor === "custom"
            ? resolvedCustomColor
            : "#4D4D4D";
  // "light" keeps its dark outline on hover; the tinted fills are borderless
  // in effect, so their border tracks the fill.
  const atcButtonHoverBorder =
    atcButtonColor === "light" || atcButtonColor === "dark"
      ? atcButtonBorder
      : `1px solid ${atcButtonHoverBg}`;
  const atcButtonTextColor = useDarkIcon ? "#333333" : "#FFFFFF";
  const [favorited, setFavorited] = useState(false);
  const [cartState, setCartState] = useState<CartState>("empty");
  const [cartQty, setCartQty] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [heartHovered, setHeartHovered] = useState(false);
  const [atcHovered, setAtcHovered] = useState(false);
  const [activeSlide, setActiveSlide] = useState<0 | 1>(0);
  const cartContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inView = useAutoplayInView(
    wrapperRef,
    (autoplayOnView && playVideoOnHoverOnly) || swipeToVideoOnTouch
  );
  const isActive = autoplayOnView ? inView : isHovered;
  const { src: imageSrc, onError: onImageError } =
    usePlaygroundImageWithFallback(product.imageUrl);
  const hasSale = product.discount != null;
  const hasDiscount = product.msrp > product.price;
  const isHlsVideo = (videoUrl ?? "").endsWith(".m3u8");
  const hasVideo = Boolean(videoUrl) && !videoFailed;
  const showVideo = swipeToVideoOnTouch
    ? hasVideo && activeSlide === 1
    : hasVideo && (!playVideoOnHoverOnly || isActive);
  const showSlideshowVideo =
    Boolean(slideshowAsset) &&
    !hasVideo &&
    (swipeToVideoOnTouch
      ? activeSlide === 1
      : !playVideoOnHoverOnly || isActive);
  const slideshowPlaying = !playVideoOnHoverOnly || isActive;
  // The swipe gallery can't defer loading until the video slide is active —
  // the slide is already sliding under the reader's thumb by then — so its
  // stream attaches as soon as the tile scrolls into view and sits paused on
  // frame 0 until the swipe lands.
  const preloadVideo = hasVideo && swipeToVideoOnTouch && inView;
  const videoSourceUrl =
    (showVideo || preloadVideo) && isHlsVideo ? (videoUrl ?? "") : "";
  useHlsVideoSource(
    videoRef,
    videoSourceUrl,
    () => setVideoFailed(true),
    showVideo
  );
  const slideshowSrc = useImageSlideshow(
    slideshowImageUrls ?? [],
    2000,
    slideshowOffsetMs
  );
  const showSlideshow = !showVideo && Boolean(slideshowSrc);
  const [galleryIndex, setGalleryIndex] = useImageGalleryIndex(
    galleryImageUrls ?? []
  );
  const showGallery = !showVideo && Boolean(galleryImageUrls?.length);

  // Detaching the stream (hover ends) leaves the element without decoded
  // pixels, so the next hover has to wait for a fresh `loadeddata` before
  // it's safe to crossfade into it again.
  useEffect(() => {
    if (!videoSourceUrl) {
      setVideoReady(false);
    }
  }, [videoSourceUrl]);

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (
        cartContainerRef.current &&
        !cartContainerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const handleAddToCart = () => {
    setCartState("loading");
    window.setTimeout(() => {
      setCartQty(ATC_QTY_OPTIONS[0]);
      setCartState("in-cart");
    }, 400);
  };

  const handleSelectQty = (qty: number) => {
    setDropdownOpen(false);
    setCartState("loading");
    window.setTimeout(() => {
      setCartQty(qty);
      setCartState("in-cart");
    }, 400);
  };

  const handleRemove = () => {
    setCartState("empty");
    setCartQty(0);
    setDropdownOpen(false);
  };

  const handleIncrement = () => {
    setCartQty((qty) => qty + ATC_QTY_STEP);
  };

  const handleDecrement = () => {
    setCartQty((qty) => {
      const next = qty - ATC_QTY_STEP;
      if (next <= 0) {
        setCartState("empty");
        return 0;
      }
      return next;
    });
  };

  const showSwipeCrossfade = hasVideo && swipeToVideoOnTouch;
  const showHoverCrossfade =
    hasVideo && playVideoOnHoverOnly && !swipeToVideoOnTouch;
  const showHoverSlideshowPlayAffordance =
    Boolean(slideshowAsset) &&
    !hasVideo &&
    playVideoOnHoverOnly &&
    !swipeToVideoOnTouch;
  // Hold the product image until the video has decoded a frame of its own.
  // Crossfading earlier would fade to an empty layer and then snap to the
  // first frame, which is the jump this gating exists to avoid.
  const hoverVideoVisible = isActive && videoReady;

  return (
    <div
      ref={wrapperRef}
      // `cursor-pointer` on the whole tile mirrors production, where the tile
      // is a link and every control inside it is interactive. The prototype's
      // CTAs have no destinations, so this is what sells them as clickable.
      className={cn(
        "group @container flex w-full min-w-0 cursor-pointer flex-col items-start gap-[8px]",
        "[&_button]:cursor-pointer [&_button:disabled]:cursor-default",
        justified ? "h-full" : ""
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn("relative w-full overflow-hidden", imageRadiusClass)}
        style={{ aspectRatio: "1 / 1" }}
      >
        {showSwipeCrossfade ? (
          <SwipeVideoSlides
            imageSrc={imageSrc}
            imageAlt={product.productName}
            videoRef={videoRef}
            videoUrl={videoUrl}
            isHlsVideo={isHlsVideo}
            activeSlide={activeSlide}
            onActiveSlideChange={setActiveSlide}
            onImageError={onImageError}
            onVideoLoadedData={() => setVideoReady(true)}
            onVideoError={() => {
              // Before the first swipe, this element has no real source yet
              // (HLS hasn't loaded, and there's no <source> for HLS), so the
              // browser fires a spurious "no supported source" error. Only
              // treat it as a genuine failure once we've actually swiped to
              // the video slide and tried to play it.
              if (!showVideo) return;
              setVideoFailed(true);
            }}
            imageRadiusClass={imageRadiusClass}
          />
        ) : showHoverCrossfade ? (
          <>
            <img
              src={imageSrc}
              alt={product.productName}
              className={cn(
                "absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-in-out",
                imageRadiusClass,
                hoverVideoVisible ? "opacity-0" : "opacity-100"
              )}
              referrerPolicy="no-referrer"
              onError={onImageError}
            />
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              className={cn(
                "absolute inset-0 h-full w-full cursor-pointer object-cover transition-opacity duration-300 ease-in-out",
                imageRadiusClass,
                hoverVideoVisible ? "opacity-100" : "opacity-0"
              )}
              onLoadedData={() => setVideoReady(true)}
              onError={() => {
                // Before the first hover, this element has no real source yet
                // (HLS hasn't loaded, and there's no <source> for HLS), so the
                // browser fires a spurious "no supported source" error. Only
                // treat it as a genuine failure once we've actually tried to
                // play the real video.
                if (!showVideo) return;
                setVideoFailed(true);
              }}
            >
              {isHlsVideo ? null : <source src={videoUrl} type="video/mp4" />}
            </video>
            {/* Play icon — bottom-right corner, fades out (with the same
                timing as the image→video crossfade) once the video starts
                playing. The 32px circle lines up with Find similar (8px
                inset). The smaller standalone triangle is inset further so
                its center matches Find similar vertically and the heart
                horizontally. */}
            <div
              className={cn(
                "pointer-events-none absolute flex items-center justify-center transition-opacity duration-300 ease-in-out",
                playIconStyle === "circle-button"
                  ? "bg-fs-surface-primary right-2 bottom-2 size-8 rounded-full shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)]"
                  : "right-[10px] bottom-[14px] size-5",
                hoverVideoVisible ? "opacity-0" : "opacity-100"
              )}
            >
              {playIconStyle === "circle-button" ? (
                <PlayTriangleIconDark />
              ) : (
                <PlayTriangleIcon />
              )}
            </div>
          </>
        ) : showVideo ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            poster={imageSrc}
            className={cn(
              "absolute inset-0 h-full w-full cursor-pointer object-cover",
              imageRadiusClass
            )}
            onError={() => setVideoFailed(true)}
          >
            {isHlsVideo ? null : <source src={videoUrl} type="video/mp4" />}
          </video>
        ) : showSlideshowVideo && slideshowAsset ? (
          <SlideshowVideo
            asset={slideshowAsset}
            alt={product.productName}
            playing={slideshowPlaying}
            playbackIndicator={slideshowPlaybackIndicator}
            className={imageRadiusClass}
          />
        ) : (
          <img
            src={
              showGallery
                ? galleryImageUrls?.[galleryIndex]
                : showSlideshow
                  ? slideshowSrc
                  : imageSrc
            }
            alt={product.productName}
            className={cn(
              "absolute inset-0 h-full w-full object-cover",
              imageRadiusClass
            )}
            referrerPolicy="no-referrer"
            onError={onImageError}
          />
        )}
        {showGallery ? (
          <HoverGalleryControls
            imageCount={galleryImageUrls?.length ?? 0}
            activeIndex={galleryIndex}
            onChange={setGalleryIndex}
          />
        ) : null}
        {showHoverSlideshowPlayAffordance ? (
          <div
            className={cn(
              "pointer-events-none absolute z-[1] flex items-center justify-center transition-opacity duration-300 ease-in-out",
              playIconStyle === "circle-button"
                ? "bg-fs-surface-primary right-2 bottom-2 size-8 rounded-full shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)]"
                : "right-[10px] bottom-[14px] size-5",
              isActive ? "opacity-0" : "opacity-100"
            )}
          >
            {playIconStyle === "circle-button" ? (
              <PlayTriangleIconDark />
            ) : (
              <PlayTriangleIcon />
            )}
          </div>
        ) : null}
        {showSlideshowVideo ? null : (
          <div
            className={cn(
              "pointer-events-none absolute inset-0",
              imageRadiusClass
            )}
            style={{ background: "rgba(51,51,51,0.03)" }}
          />
        )}
        {showVideo && slideshowPlaybackIndicator === "bars" ? (
          <VideoPlaybackBar
            videoRef={videoRef}
            className={
              showHoverCrossfade
                ? cn(
                    "transition-opacity duration-300 ease-in-out",
                    hoverVideoVisible ? "opacity-100" : "opacity-0"
                  )
                : undefined
            }
          />
        ) : null}

        {product.isBestseller ? (
          <div className="bg-fs-surface-primary absolute top-[8px] left-[8px] rounded-[2px] px-[8px] py-0">
            <span
              className="text-[12px] leading-[16px] tracking-[0.15px] whitespace-nowrap text-[#333]"
              style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
            >
              Bestseller
            </span>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => setFavorited((f) => !f)}
          onMouseEnter={
            disableHoverAffordances ? undefined : () => setHeartHovered(true)
          }
          onMouseLeave={
            disableHoverAffordances ? undefined : () => setHeartHovered(false)
          }
          className="absolute top-2 right-2 flex size-6 items-center justify-center"
          aria-label="Favorite"
        >
          <HeartIcon filled={favorited} hovered={heartHovered} />
        </button>

        {showFindSimilarOnImage ? (
          findSimilarExpandsOnHover && !disableHoverAffordances ? (
            <button
              type="button"
              onClick={onFindSimilarClick}
              className={cn(
                "absolute bottom-[8px] left-[8px] flex h-[32px] w-[32px] items-center justify-start overflow-hidden rounded-full transition-[width] duration-200 ease-out",
                "shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)] hover:shadow-[0_0_0_1px_#222]",
                "group-hover:w-[124px]"
              )}
              style={{ background: "#FFFFFF" }}
              aria-label="Find similar"
            >
              <span className="flex items-center gap-[6px] pr-[8px] pl-[8px] whitespace-nowrap">
                <FindSimilarIcon />
                {/* Opacity (not just the button's width/overflow-hidden clip) hides this text at rest — the collapsed 32px width only leaves ~2px of clipping margin past the icon, so without this the label's first letter peeks out past the circle. */}
                <span
                  className="text-[14px] leading-[20px] tracking-[0.15px] text-[#333] opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"
                  style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
                >
                  Find similar
                </span>
              </span>
            </button>
          ) : (
            // Touch/mobile tiles have no meaningful hover-to-expand gesture,
            // so this variant stays a fixed-size icon button. It keeps a
            // hover outline for pointer/trackpad users unless the caller
            // opted out of hover affordances entirely.
            <button
              type="button"
              onClick={onFindSimilarClick}
              className={cn(
                "absolute bottom-[8px] left-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)]",
                imageControlHoverClass
              )}
              style={{ background: "#FFFFFF" }}
              aria-label="Find similar"
            >
              <FindSimilarIcon />
            </button>
          )
        ) : null}

        {showAddToCartOnImage ? (
          <button
            type="button"
            onClick={cartState === "in-cart" ? handleRemove : handleAddToCart}
            disabled={cartState === "loading"}
            className={cn(
              "absolute right-[8px] bottom-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full",
              "shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)]",
              imageControlHoverClass
            )}
            style={{ background: "#FFFFFF" }}
            aria-label={
              cartState === "in-cart" ? "Remove from cart" : "Add to cart"
            }
          >
            {cartState === "loading" ? (
              <SpinnerIconDark />
            ) : cartState === "in-cart" ? (
              <CheckIcon />
            ) : (
              <PlusIconDark />
            )}
          </button>
        ) : null}
      </div>

      {/* Full-bleed native-app mobile grids (`squareCorners`) get 8px of
          horizontal padding so metadata text doesn't run up against the
          screen edge; desktop keeps its existing flush edges. */}
      <div
        className={cn(
          "flex w-full flex-col items-start gap-[4px]",
          justified ? "flex-1" : "",
          squareCorners ? "px-2" : ""
        )}
      >
        <div className="flex w-full flex-col items-start gap-0">
          {product.swatchColors.length > 0 ? (
            <div className="mt-[2px] mb-[5px] flex items-center gap-[4px]">
              {product.swatchColors.map((color, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 rounded-full border border-(--slate-color-grey-1000)/10"
                  style={{
                    backgroundColor: color,
                    width: swatchSizePx,
                    height: swatchSizePx,
                  }}
                />
              ))}
              {product.extraSwatches > 0 ? (
                <span
                  className="w-[19px] text-center text-[12px] leading-[16px] tracking-[0.15px] text-[#757575]"
                  style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
                >
                  +{product.extraSwatches}
                </span>
              ) : null}
            </div>
          ) : null}

          {condensedMetadata ? (
            <div className="mb-px flex w-full min-w-0 items-baseline gap-[4px]">
              <span
                className={cn(
                  "flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px]",
                  hasSale ? "text-[#7d3e1e]" : "text-[#333]"
                )}
                style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
              >
                $
                {product.price % 1 === 0
                  ? product.price
                  : product.price.toFixed(2)}
              </span>
              <span
                className="min-w-0 truncate text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
                style={{ fontFamily: "Graphik, sans-serif", fontWeight: 400 }}
              >
                {product.productName}
              </span>
            </div>
          ) : (
            <>
              <div className="flex w-full items-baseline gap-[8px]">
                <span
                  className={cn(
                    "flex-shrink-0 text-[18px] leading-[26px] tracking-[0.15px]",
                    hasSale ? "text-[#7d3e1e]" : "text-[#333]"
                  )}
                  style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
                >
                  $
                  {product.price % 1 === 0
                    ? product.price
                    : product.price.toFixed(2)}
                </span>
                {hasSale && hasDiscount ? (
                  <span
                    className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#757575] line-through"
                    style={{
                      fontFamily: "Graphik, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    $
                    {product.msrp % 1 === 0
                      ? product.msrp
                      : product.msrp.toFixed(2)}
                  </span>
                ) : null}
                {hasDiscount ? (
                  <span
                    className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
                    style={{
                      fontFamily: "Graphik, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    MSRP ${Math.round(product.msrp)}
                  </span>
                ) : null}
              </div>

              <p
                className={cn(
                  "w-full text-[14px] leading-[20px] tracking-[0.15px] text-[#333]",
                  productNameLineClamp === 1 ? "line-clamp-1" : "line-clamp-2"
                )}
                style={{ fontFamily: "Graphik, sans-serif", fontWeight: 500 }}
              >
                {product.productName}
              </p>

              {product.productMetadata ? (
                <p
                  className="mt-[1px] text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
                  style={{
                    fontFamily: "Graphik, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {product.productMetadata}
                </p>
              ) : null}
            </>
          )}
        </div>

        {/* Extra 4px on top of the metadata block's own gap, matching
            production's spacing between the product name and brand line. */}
        <div
          className={cn(
            "flex w-full flex-col items-start gap-[1px]",
            condensedMetadata ? "mt-0" : "mt-[4px]"
          )}
        >
          <div className="w-full overflow-hidden pb-[2px]">
            <span className="type-fs-paragraph block overflow-hidden text-ellipsis whitespace-nowrap text-[#333] underline underline-offset-[2px]">
              {product.brandName}
            </span>
          </div>

          {condensedMetadata ? null : (
            <div className="flex w-full items-center gap-[4px]">
              <span className="type-fs-paragraph flex-shrink-0 whitespace-nowrap text-[#333]">
                {product.minOrder}
              </span>
              {product.rating != null && product.rating > 0 ? (
                <>
                  <span
                    aria-hidden
                    className="type-fs-paragraph flex-shrink-0 text-[#333]"
                  >
                    ·
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-[4px]">
                    <StarIcon />
                    <span className="type-fs-paragraph whitespace-nowrap text-[#333]">
                      {product.rating.toFixed(1)}
                      {product.reviewCount != null && product.reviewCount > 0
                        ? ` (${product.reviewCount})`
                        : ""}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {product.programTagChips.length > 0 ? (
            <ProgramAndSearchTagsRow
              programChips={product.programTagChips}
              searchMatchLabels={product.searchMatchTagLabels}
              showSearchMatchTags={false}
            />
          ) : null}

          {showPromotedLabel && !showPromotedBelowAtc ? (
            <p className="mt-[2px] text-[11px] leading-[16px] tracking-[0.15px] text-[#757575]">
              <PlaygroundLocalMsg defaultMessage="Promoted" />
            </p>
          ) : null}

          {product.freeShipping && product.freeShippingLabel !== undefined ? (
            shippingAsIconLine ? (
              <div className="flex w-full min-w-0 items-center gap-[4px]">
                <ShippingPerkIcon />
                <span className="type-fs-paragraph min-w-0 truncate text-[#36676a]">
                  <PlaygroundLocalMsg
                    defaultMessage="Free shipping over {threshold}"
                    values={{ threshold: "$400" }}
                  />
                  {product.discount != null && product.discount !== "" ? (
                    <>
                      {" "}
                      <PlaygroundLocalMsg
                        defaultMessage="+ {discount}"
                        values={{ discount: product.discount }}
                      />
                    </>
                  ) : null}
                </span>
              </div>
            ) : (
              <FreeShippingBadge label={product.freeShippingLabel} />
            )
          ) : null}
        </div>
      </div>

      {rationale !== undefined && rationale.length > 0 ? (
        <TileRationale bullets={rationale} fillHeight={justified} />
      ) : null}

      {showAddToCartOnImage && showFindSimilarOnImage ? (
        showPromotedLabel && showPromotedBelowAtc ? (
          <p className="mt-(--slate-spacing-element-default) text-[11px] leading-[16px] tracking-[0.15px] text-[#757575]">
            <PlaygroundLocalMsg defaultMessage="Promoted" />
          </p>
        ) : null
      ) : (
        <div
          className={cn(
            "flex w-full min-w-0 flex-col items-start",
            showPromotedLabel && showPromotedBelowAtc
              ? "gap-(--slate-spacing-element-default)"
              : "gap-[2px]",
            compactAtcSpacing ? "-mt-[4px]" : "",
            squareCorners ? "px-2" : ""
          )}
        >
          <div
            className={cn(
              "w-full min-w-0 items-start",
              fillButtonWidth
                ? showAddToCartOnImage || showFindSimilarOnImage
                  ? "grid grid-cols-1"
                  : "grid grid-cols-2 gap-[8px]"
                : "flex flex-wrap gap-[6px]"
            )}
          >
            {showAddToCartOnImage ? null : (
              <div
                ref={cartContainerRef}
                className={cn("relative", fillButtonWidth ? "min-w-0" : "")}
              >
                {cartState === "empty" ? (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    onMouseEnter={() => setAtcHovered(true)}
                    onMouseLeave={() => setAtcHovered(false)}
                    className={cn(
                      "inline-flex items-center justify-center gap-[6px] rounded-[4px] px-[8px] py-[6px] transition-colors",
                      fillButtonWidth ? "w-full" : "shrink-0"
                    )}
                    style={{
                      background: atcHovered ? atcButtonHoverBg : atcButtonBg,
                      border: atcHovered
                        ? atcButtonHoverBorder
                        : atcButtonBorder,
                    }}
                  >
                    {useDarkIcon ? <PlusIconDark /> : <PlusIcon />}
                    <span
                      className="text-[14px] leading-[20px] tracking-[0.15px] whitespace-nowrap"
                      style={{
                        fontFamily: GRAPHIK,
                        fontWeight: 400,
                        color: atcButtonTextColor,
                      }}
                    >
                      Add to cart
                    </span>
                  </button>
                ) : null}

                {cartState === "loading" ? (
                  <button
                    type="button"
                    aria-busy
                    disabled
                    className={cn(
                      "inline-flex items-center justify-center rounded-[4px] px-[8px] py-[6px]",
                      fillButtonWidth ? "w-full" : "shrink-0"
                    )}
                    style={{
                      background: atcButtonBg,
                      border: atcButtonBorder,
                      minWidth: fillButtonWidth ? undefined : 96,
                      minHeight: 34,
                    }}
                  >
                    {useDarkIcon ? <SpinnerIconDark /> : <SpinnerIcon />}
                  </button>
                ) : null}

                {cartState === "in-cart" && showAddToCartPicker ? (
                  <div
                    className={cn(
                      "inline-flex items-center rounded-[4px]",
                      fillButtonWidth ? "w-full justify-between" : "shrink-0"
                    )}
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #333333",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleDecrement}
                      className="flex items-center justify-center px-[8px] py-[8px]"
                      aria-label="Decrease quantity"
                    >
                      <MinusIconDark />
                    </button>
                    <span
                      className="text-center text-[14px] leading-[20px] tracking-[0.15px] whitespace-nowrap text-[#333]"
                      style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                    >
                      <AnimatedCartQty qty={cartQty} /> in cart
                    </span>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      className="flex items-center justify-center px-[8px] py-[8px]"
                      aria-label="Increase quantity"
                    >
                      <PlusIconDark />
                    </button>
                  </div>
                ) : null}

                {cartState === "in-cart" && !showAddToCartPicker ? (
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((open) => !open)}
                    className={cn(
                      "inline-flex items-center justify-center gap-[6px] rounded-[4px] px-[8px] py-[6px] transition-colors",
                      fillButtonWidth ? "w-full" : "shrink-0"
                    )}
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #333333",
                    }}
                  >
                    <span
                      className="text-[14px] leading-[20px] tracking-[0.15px] whitespace-nowrap text-[#333]"
                      style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                    >
                      <AnimatedCartQty qty={cartQty} /> in cart
                    </span>
                    <ChevronDownIcon />
                  </button>
                ) : null}

                {cartState === "in-cart" &&
                !showAddToCartPicker &&
                dropdownOpen ? (
                  <div
                    className="bg-fs-surface-primary absolute top-full left-0 z-10 mt-[4px] overflow-hidden rounded-[4px]"
                    style={{
                      border: "1px solid #DFE0E1",
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                      minWidth: 140,
                    }}
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={handleRemove}
                      className="block w-full px-[16px] py-[12px] text-left text-[14px] leading-[20px] tracking-[0.15px] whitespace-nowrap text-[#333] hover:bg-[#F1F1F1]"
                      style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                      role="menuitem"
                    >
                      Remove from cart
                    </button>
                    {ATC_QTY_OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleSelectQty(option)}
                        className="block w-full px-[16px] py-[12px] text-left text-[14px] leading-[20px] tracking-[0.15px] whitespace-nowrap text-[#333] hover:bg-[#F1F1F1]"
                        style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                        role="menuitem"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {showFindSimilarOnImage ? null : (
              <button
                type="button"
                onClick={onFindSimilarClick}
                className={cn(
                  "inline-flex items-center justify-center rounded-[4px] px-[8px] py-[6px] transition-colors",
                  fillButtonWidth ? "w-full min-w-0" : "shrink-0"
                )}
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #DFE0E1",
                }}
              >
                <span
                  className="text-[12px] leading-[16px] tracking-[0.15px] whitespace-nowrap text-[#333]"
                  style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                >
                  Find similar
                </span>
              </button>
            )}
          </div>
          {showPromotedLabel && showPromotedBelowAtc ? (
            <p className="text-[11px] leading-[16px] tracking-[0.15px] text-[#757575]">
              <PlaygroundLocalMsg defaultMessage="Promoted" />
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Why the assistant kept this tile.
 *
 * The `@[210px]` container query is production's (from `compass/src/product-comparison/Section/ComparisonCardBodyText`):
 * below that width the rule plus indent leaves too little room for the text,
 * so it drops out entirely rather than wrapping to slivers. `ProductTileATC`'s
 * wrapper carries `@container` to scope it.
 *
 * Text wraps naturally and the rule only spans the bullet text itself
 * (`border-l` lives on the inner block, sized to its own content), so
 * leftover space renders as plain whitespace below the rule rather than
 * stretching it.
 *
 * `fillHeight` (passed whenever the tile is `justified`, i.e. stretched to a
 * shared row height by the CSS grid) fixes the block to a constant 3-line
 * box. Combined with the metadata block above it taking the flex-grow, that
 * pins both the first bullet AND the buttons below to the same y-position on
 * every tile in the row — variable bullet counts and wrap lengths would
 * otherwise stagger them. Overflow past 3 lines is clipped rather than
 * allowed to push the row out of alignment.
 */
function TileRationale({
  bullets,
  fillHeight = false,
}: {
  bullets: string[];
  fillHeight?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "mt-2 hidden w-full flex-col items-start @[210px]:flex",
        fillHeight ? "h-[3lh] overflow-hidden" : ""
      )}
    >
      <div className="text-fs-text-primary m-0 flex flex-col gap-1 border-l-[3px] border-[#d9d9d9] pl-(--slate-dimensions-3)">
        {bullets.map((bullet) => (
          <div key={bullet} className="type-fs-paragraph">
            {bullet}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProductTileATCCompact({
  product,
  justified = true,
  onFindSimilarClick,
  useDefaultMetadataSize = false,
}: ProductTileProps): React.ReactElement {
  const [favorited, setFavorited] = useState(false);
  const [heartHovered, setHeartHovered] = useState(false);
  const [cartState, setCartState] = useState<CartState>("empty");
  const { src: imageSrc, onError: onImageError } =
    usePlaygroundImageWithFallback(product.imageUrl);
  const hasSale = product.discount != null;
  const hasDiscount = product.msrp > product.price;

  const handleAddClick = () => {
    if (cartState === "in-cart") {
      setCartState("empty");
      return;
    }
    if (cartState === "loading") {
      return;
    }
    setCartState("loading");
    window.setTimeout(() => {
      setCartState("in-cart");
    }, 400);
  };

  return (
    <div
      className={cn(
        "flex w-full min-w-0 cursor-pointer flex-col items-start gap-[8px]",
        "[&_button]:cursor-pointer [&_button:disabled]:cursor-default",
        justified ? "h-full" : ""
      )}
    >
      <div
        className="relative w-full overflow-hidden rounded-[4px]"
        style={{ aspectRatio: "1 / 1" }}
      >
        <img
          src={imageSrc}
          alt={product.productName}
          className="absolute inset-0 h-full w-full rounded-[4px] object-cover"
          referrerPolicy="no-referrer"
          onError={onImageError}
        />
        <div
          className="absolute inset-0 rounded-[4px]"
          style={{ background: "rgba(51,51,51,0.03)" }}
        />

        {product.isBestseller ? (
          <div className="bg-fs-surface-primary absolute top-[8px] left-[8px] rounded-[2px] px-[8px] py-0">
            <span
              className="text-[12px] leading-[16px] tracking-[0.15px] whitespace-nowrap text-[#333]"
              style={{ fontFamily: GRAPHIK, fontWeight: 500 }}
            >
              Bestseller
            </span>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => setFavorited((f) => !f)}
          onMouseEnter={() => setHeartHovered(true)}
          onMouseLeave={() => setHeartHovered(false)}
          className="absolute top-2 right-2 flex size-6 items-center justify-center"
          aria-label="Favorite"
        >
          <HeartIcon filled={favorited} hovered={heartHovered} />
        </button>

        <button
          type="button"
          onClick={onFindSimilarClick}
          className="absolute bottom-[8px] left-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full"
          style={{
            background: "#FFFFFF",
            boxShadow: "0px 0px 1px 0px rgba(0,0,0,0.15)",
          }}
          aria-label="Find similar"
        >
          <FindSimilarIcon />
        </button>

        <button
          type="button"
          onClick={handleAddClick}
          aria-busy={cartState === "loading"}
          aria-label={
            cartState === "in-cart" ? "Remove from cart" : "Add to cart"
          }
          className="absolute right-[8px] bottom-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-full"
          style={{
            background: "#FFFFFF",
            boxShadow: "0px 0px 1px 0px rgba(0,0,0,0.15)",
          }}
        >
          {cartState === "empty" ? (
            <PlusIconDark />
          ) : cartState === "loading" ? (
            <SpinnerIconDark />
          ) : (
            <CheckIcon />
          )}
        </button>
      </div>

      <div
        className={cn(
          "flex w-full flex-col items-start gap-[4px]",
          justified ? "flex-1" : ""
        )}
      >
        <div className="flex w-full flex-col items-start gap-0">
          {product.swatchColors.length > 0 ? (
            <div className="mt-[2px] mb-[5px] flex items-center gap-[4px]">
              {product.swatchColors.map((color, i) => (
                <div
                  key={i}
                  className="h-[16px] w-[16px] flex-shrink-0 rounded-full border border-(--slate-color-grey-1000)/10"
                  style={{ backgroundColor: color }}
                />
              ))}
              {product.extraSwatches > 0 ? (
                <span
                  className="w-[19px] text-center text-[12px] leading-[16px] tracking-[0.15px] text-[#757575]"
                  style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
                >
                  +{product.extraSwatches}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="flex w-full items-baseline gap-[8px]">
            <span
              className={cn(
                "flex-shrink-0 text-[18px] leading-[26px] tracking-[0.15px]",
                hasSale ? "text-[#7d3e1e]" : "text-[#333]"
              )}
              style={{ fontFamily: GRAPHIK, fontWeight: 500 }}
            >
              $
              {product.price % 1 === 0
                ? product.price
                : product.price.toFixed(2)}
            </span>
            {hasSale && hasDiscount ? (
              <span
                className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#757575] line-through"
                style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
              >
                $
                {product.msrp % 1 === 0
                  ? product.msrp
                  : product.msrp.toFixed(2)}
              </span>
            ) : null}
            {hasDiscount ? (
              <span
                className="flex-shrink-0 text-[12px] leading-[16px] tracking-[0.15px] text-[#333]"
                style={{ fontFamily: GRAPHIK, fontWeight: 400 }}
              >
                MSRP ${Math.round(product.msrp)}
              </span>
            ) : null}
          </div>

          <p
            className="w-full truncate text-[14px] leading-[20px] tracking-[0.15px] text-[#333]"
            style={{ fontFamily: GRAPHIK, fontWeight: 500 }}
          >
            {product.productName}
          </p>
        </div>

        <div className="flex w-full flex-col items-start gap-[1px]">
          <div className="w-full overflow-hidden pb-[2px]">
            <span className="type-fs-paragraph block overflow-hidden text-ellipsis whitespace-nowrap text-[#333] underline underline-offset-[2px]">
              {product.brandName}
            </span>
          </div>

          <div className="flex w-full items-center gap-[4px]">
            <span
              className={cn(
                "flex-shrink-0 whitespace-nowrap text-[#333]",
                useDefaultMetadataSize
                  ? "type-fs-paragraph"
                  : "text-[12px] leading-[16px] tracking-[0.15px]"
              )}
              style={
                useDefaultMetadataSize
                  ? undefined
                  : { fontFamily: GRAPHIK, fontWeight: 400 }
              }
            >
              {product.minOrder}
            </span>
            {product.rating != null ? (
              <>
                <span
                  aria-hidden
                  className={cn(
                    "flex-shrink-0 text-[#333]",
                    useDefaultMetadataSize
                      ? "type-fs-paragraph"
                      : "text-[12px] leading-[16px] tracking-[0.15px]"
                  )}
                  style={
                    useDefaultMetadataSize
                      ? undefined
                      : { fontFamily: GRAPHIK, fontWeight: 400 }
                  }
                >
                  ·
                </span>
                <div className="flex flex-shrink-0 items-center gap-[4px]">
                  <StarIcon />
                  <span
                    className={cn(
                      "whitespace-nowrap text-[#333]",
                      useDefaultMetadataSize
                        ? "type-fs-paragraph"
                        : "text-[12px] leading-[16px] tracking-[0.15px]"
                    )}
                    style={
                      useDefaultMetadataSize
                        ? undefined
                        : { fontFamily: GRAPHIK, fontWeight: 400 }
                    }
                  >
                    {product.rating.toFixed(1)}
                  </span>
                </div>
              </>
            ) : null}
          </div>

          {product.programTagChips.length > 0 ? (
            <ProgramAndSearchTagsRow
              programChips={product.programTagChips}
              searchMatchLabels={product.searchMatchTagLabels}
              showSearchMatchTags={false}
            />
          ) : null}

          {product.freeShipping && product.freeShippingLabel !== undefined ? (
            <FreeShippingBadge label={product.freeShippingLabel} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function PlusIconDark() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.00009 1V11M1 5.96923H11"
        stroke="#333333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MinusIconDark() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 5.96923H11"
        stroke="#333333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AnimatedCartQty({ qty }: { qty: number }): React.ReactElement {
  const ref = useRef<HTMLSpanElement>(null);
  const prevQty = useRef(qty);

  useEffect(() => {
    if (qty !== prevQty.current && ref.current) {
      const isIncrement = qty > prevQty.current;
      ref.current.animate(
        [
          {
            opacity: 0,
            transform: `translateY(${isIncrement ? 6 : -6}px)`,
          },
          { opacity: 1, transform: "translateY(0)" },
        ],
        { duration: 250, easing: "ease-out" }
      );
      prevQty.current = qty;
    }
  }, [qty]);

  return (
    <span ref={ref} className="inline-block">
      {qty}
    </span>
  );
}

export function SpinnerIconDark() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="animate-spin"
    >
      <path
        d="M7 2A5 5 0 1 1 2 7"
        stroke="#333333"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
